import { useNavigate } from "react-router-dom";
import { InteractiveCard } from "../../../components/PromotionIQ/InteractiveCard";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import {
  retailers,
  historicalPromotions,
  plannedPromotions,
  budgetGuardrails,
  getTotalTradeSpend,
  getTotalMargin,
  getPromotionsAtRisk,
  getAverageROI,
  formatCurrency,
  formatPercent,
  companyInfo,
} from "../../../data/PromotionIQ/PromotionData";
import { FileText, TrendingUp, AlertTriangle, Target, DollarSign, Users, ChevronRight, Zap } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import React from "react";

const Summary = () => {
  const navigate = useNavigate();
  const totalSpend = getTotalTradeSpend();
  const totalMargin = getTotalMargin();
  const atRisk = getPromotionsAtRisk();
  const avgROI = getAverageROI();
  const totalPromotions = historicalPromotions.length + plannedPromotions.length;

  // Budget utilization
  const budgetUsed = totalSpend / budgetGuardrails.total_trade_budget;

  // Retailer allocation data
  const retailerAllocation = budgetGuardrails.allocations.map((a, index) => {
    const retailer = retailers.find((r) => r.retailer_id === a.retailer_id);
    // Wegman's Market (R1) stays green, others are yellow/amber
    const isWegmans = a.retailer_id === "R1";
    const yellowColors = ["hsl(45, 93%, 47%)", "hsl(38, 92%, 50%)", "hsl(32, 95%, 55%)", "hsl(28, 90%, 52%)"];
    return {
      name: retailer?.name || a.retailer_id,
      retailer_id: a.retailer_id,
      value: a.allocated_pct,
      color: isWegmans ? "hsl(142, 70%, 45%)" : yellowColors[index % yellowColors.length],
    };
  });

  // Product line allocation
  const productLineData = [
    { name: "Mediterranean", value: 40, color: "hsl(32, 95%, 55%)" },
    { name: "Central American", value: 35, color: "hsl(173, 80%, 40%)" },
    { name: "Asian", value: 25, color: "hsl(220, 60%, 50%)" },
  ];

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="Executive / Board Summary"
        question="What story do I tell?"
      />

      {/* Hero Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
        <InteractiveCard
          label="YTD Revenue"
          value={formatCurrency(companyInfo.totalSales2023)}
          subValue={`+${companyInfo.yoyGrowth.toFixed(1)}% YoY`}
          variant="highlight"
          linkTo="/promotioniq"
          linkLabel="View command"
        />
        <InteractiveCard
          label="Trade Investment"
          value={formatCurrency(totalSpend)}
          subValue={`${formatPercent(budgetUsed)} of budget`}
          variant="default"
          linkTo="/promotioniq/promotion-portfolio"
          linkLabel="View portfolio"
        />
        <InteractiveCard
          label="Net Margin"
          value={formatCurrency(totalMargin)}
          subValue="Incremental profit"
          variant={totalMargin > 0 ? "success" : "warning"}
          linkTo="/promotioniq/learnings"
          linkLabel="See learnings"
        />
        <InteractiveCard
          label="Portfolio ROI"
          value={`${avgROI.toFixed(2)}x`}
          subValue="Revenue per $1 invested"
          variant={avgROI >= 1.2 ? "success" : "default"}
          linkTo="/promotioniq/retailer-investment"
          linkLabel="Compare"
        />
        <InteractiveCard
          label="Active Promotions"
          value={totalPromotions}
          subValue={`${atRisk.length} at risk`}
          variant={atRisk.length > 0 ? "warning" : "success"}
          linkTo="/promotioniq/monitoring"
          linkLabel="Monitor"
        />
      </div>

      {/* Summary Sections */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Portfolio Health */}
        <div 
          onClick={() => navigate("/promotioniq/promotion-portfolio")}
          className="border border-border bg-card p-6 cursor-pointer hover:border-primary transition-all group"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              <h3 className="section-header">Portfolio Health</h3>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Completed Promotions</span>
              <span className="font-medium">{historicalPromotions.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Planned Promotions</span>
              <span className="font-medium">{plannedPromotions.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">At-Risk Promotions</span>
              <span className={atRisk.length > 0 ? "font-medium text-warning" : "font-medium text-success"}>
                {atRisk.length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Budget Utilization</span>
              <span className="font-medium">{formatPercent(budgetUsed)}</span>
            </div>
            <div className="h-2 w-full bg-muted mt-2">
              <div
                className="h-2 bg-primary"
                style={{ width: `${Math.min(budgetUsed * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Budget Allocation by Retailer */}
        <div className="border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="h-5 w-5 text-primary" />
            <h3 className="section-header">Budget Allocation</h3>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="h-[150px] w-[150px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={retailerAllocation}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={60}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {retailerAllocation.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(220, 18%, 13%)",
                      border: "1px solid hsl(220, 15%, 25%)",
                      borderRadius: "0",
                    }}
                    formatter={(value: number) => formatPercent(value)}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {retailerAllocation.map((item) => (
                <div 
                  key={item.name} 
                  onClick={() => navigate(`/promotioniq/retailer-economics/${item.retailer_id}`)}
                  className="flex items-center justify-between text-sm cursor-pointer hover:bg-primary/10 p-1 -m-1 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2" style={{ backgroundColor: item.color }} />
                    <span className="hover:text-primary">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{formatPercent(item.value)}</span>
                    <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Retailer Performance Summary */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h3 className="section-header">Retailer Performance</h3>
          </div>
          <button 
            onClick={() => navigate("/promotioniq/retailer-investment")}
            className="btn-athena text-sm"
          >
            Full Analysis
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {retailers.map((r) => (
            <div 
              key={r.retailer_id} 
              onClick={() => navigate(`/promotioniq/retailer-economics/${r.retailer_id}`)}
              className="flex items-center justify-between p-4 bg-muted/30 cursor-pointer hover:bg-primary/10 transition-all group"
            >
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">{r.name}</p>
                <p className="text-sm text-muted-foreground">
                  {formatCurrency(r.ytd_sales || 0)} YTD
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className={`text-2xl font-bold ${r.roi_index >= 1 ? "text-success" : "text-warning"}`}>
                    {r.roi_index.toFixed(2)}x
                  </p>
                  <p className="text-xs text-muted-foreground">ROI Index</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product Line Performance */}
      <div className="border border-border bg-card p-6">
        <h3 className="section-header mb-4">Product Line Performance</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {productLineData.map((product) => (
            <div 
              key={product.name} 
              onClick={() => navigate("/promotioniq/baseline-diagnostics")}
              className="p-4 bg-muted/30 cursor-pointer hover:bg-primary/10 transition-all group"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="h-3 w-3" style={{ backgroundColor: product.color }} />
                <span className="font-medium group-hover:text-primary transition-colors">{product.name}</span>
                <span className="text-lg font-bold text-primary">{product.value}%</span>
              </div>
              <p className="text-xs text-muted-foreground text-right">of revenue share</p>
            </div>
          ))}
        </div>
      </div>

      {/* Key Insights */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Positive Insights */}
        <div className="border border-success/30 bg-success/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-success" />
              <h3 className="font-semibold text-success">What's Working</h3>
            </div>
            <button 
              onClick={() => navigate("/promotioniq/learnings")}
              className="text-xs text-success hover:underline flex items-center gap-1"
            >
              See all learnings <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          <ul className="space-y-3">
            <li 
              onClick={() => navigate("/promotioniq/retailer-economics/R1")}
              className="flex items-start gap-2 text-sm cursor-pointer hover:bg-success/10 p-1 -m-1 transition-colors"
            >
              <span className="text-success">✓</span>
              <span>Wegman’s Market delivering 1.42x ROI with premium positioning</span>
            </li>
            <li 
              onClick={() => navigate("/promotioniq/baseline-diagnostics")}
              className="flex items-start gap-2 text-sm cursor-pointer hover:bg-success/10 p-1 -m-1 transition-colors"
            >
              <span className="text-success">✓</span>
              <span>Mediterranean line outperforming with 40% revenue share</span>
            </li>
            <li 
              onClick={() => navigate("/promotioniq/impact-panel")}
              className="flex items-start gap-2 text-sm cursor-pointer hover:bg-success/10 p-1 -m-1 transition-colors"
            >
              <span className="text-success">✓</span>
              <span>TikTok + Instagram combo showing 35% lift on Mediterranean</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <span className="text-success">✓</span>
              <span>+31.3% YoY growth exceeding category average</span>
            </li>
          </ul>
        </div>

        {/* Areas for Improvement */}
        <div className="border border-warning/30 bg-warning/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              <h3 className="font-semibold text-warning">Areas for Improvement</h3>
            </div>
            <button 
              onClick={() => navigate("/promotioniq/monitoring")}
              className="text-xs text-warning hover:underline flex items-center gap-1"
            >
              View monitoring <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          <ul className="space-y-3">
            <li 
              onClick={() => navigate("/promotioniq/retailer-economics/R2")}
              className="flex items-start gap-2 text-sm cursor-pointer hover:bg-warning/10 p-1 -m-1 transition-colors"
            >
              <span className="text-warning">!</span>
              <span>Whole Foods Market weekly promotions showing pantry loading risk</span>
            </li>
            <li 
              onClick={() => navigate("/promotioniq/baseline-diagnostics")}
              className="flex items-start gap-2 text-sm cursor-pointer hover:bg-warning/10 p-1 -m-1 transition-colors"
            >
              <span className="text-warning">!</span>
              <span>Asian line underperforming at 25% share (target: 30%)</span>
            </li>
            <li 
              onClick={() => navigate("/promotioniq/negotiation")}
              className="flex items-start gap-2 text-sm cursor-pointer hover:bg-warning/10 p-1 -m-1 transition-colors"
            >
              <span className="text-warning">!</span>
              <span>Deep discounts (&gt;18%) causing margin decay at Whole Foods Market</span>
            </li>
            <li 
              onClick={() => navigate("/promotioniq/monitoring")}
              className="flex items-start gap-2 text-sm cursor-pointer hover:bg-warning/10 p-1 -m-1 transition-colors"
            >
              <span className="text-warning">!</span>
              <span>{atRisk.length} promotions require immediate attention</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Strategic Recommendations */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <h3 className="section-header mb-4">Strategic Recommendations</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div 
            onClick={() => navigate("/promotioniq/monitoring")}
            className="p-4 bg-card border border-border cursor-pointer hover:border-primary transition-all group"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium text-primary">Near-term (30 days)</p>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <ul className="space-y-1 text-sm">
              <li>• Review P205 high-risk Whole Foods Market promotion</li>
              <li>• Cap Whole Foods Market discount at 18%</li>
              <li>• Increase TikTok spend for Mediterranean</li>
            </ul>
          </div>
          <div 
            onClick={() => navigate("/promotioniq/scenarios")}
            className="p-4 bg-card border border-border cursor-pointer hover:border-primary transition-all group"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium text-primary">Medium-term (90 days)</p>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <ul className="space-y-1 text-sm">
              <li>• Boost Asian line promotion frequency</li>
              <li>• Expand Instagram campaigns to all lines</li>
              <li>• Negotiate better display placements at Whole Foods Market</li>
            </ul>
          </div>
          <div 
            onClick={() => navigate("/promotioniq/negotiation")}
            className="p-4 bg-card border border-border cursor-pointer hover:border-primary transition-all group"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium text-primary">Long-term (180 days)</p>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <ul className="space-y-1 text-sm">
              <li>• Develop retailer-specific playbooks</li>
              <li>• Explore Costco expansion for Asian line</li>
              <li>• Build automated social ROI tracking</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Export Action */}
      <div className="flex items-center justify-between border border-border bg-card p-4">
        <div>
          <p className="font-medium">Ready to share this summary?</p>
          <p className="text-sm text-muted-foreground">Export for board meetings, JBP sessions, or internal alignment</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate("/promotioniq")}
            className="btn-athena"
          >
            <Zap className="h-4 w-4 mr-2" />
            Back to Command
          </button>
          <button className="btn-athena-filled">
            <FileText className="h-4 w-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default Summary;
